import { findSpots, type Point, type Spot } from '@trippath/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SpotMap } from './SpotMap';
import { PAUSE } from './TownField';

interface Props {
  /** Where the town is: places near it come first. None until a town is picked. */
  near: Point | null;
  /** The exact place picked, if one is. */
  spot: Spot | null;
  onPick: (spot: Spot) => void;
  /** Back to the town itself; none where a place found is not kept in the field. */
  onClear?: () => void;
  /** What it is for, when not a stop's exact place. */
  label?: string;
  /** Drawn as the plan's search: a round field with a lens, what it is for said inside it. */
  search?: boolean;
}

/**
 * A place typed by its GPS, `35.5494, 139.7798`: a stop no map names, said where it is; `null`
 * for anything else.
 */
export function gpsOf(typed: string): Spot | null {
  const found = /^\s*(-?\d{1,2}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/.exec(typed);
  const lat = Number(found?.[1]);
  const lon = Number(found?.[2]);
  if (!found || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return gpsSpot(lat, lon);
}

/** A spot no map names, said by its GPS: `35.54940, 139.77980`. */
export function gpsSpot(lat: number, lon: number): Spot {
  return { lat, lon, name: `${lat.toFixed(5)}, ${lon.toFixed(5)}`, detail: 'GPS' };
}

type Found = { query: string; spots: Spot[]; osm: 'ok' | 'short' | 'off' | 'failed' } | null;

/** A folded map, as a place is picked on one. */
export function MapIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14.1 5.9 9 3 3 6v15l6-3 6 3 6-3V3l-6.9 2.9ZM9 3v15M15 6v15" />
    </svg>
  );
}

/**
 * The exact place of a stop within its town, when it is not the town itself: an airport, a bus
 * station, the address a car leaves from. Found in OpenStreetMap, near the town first.
 */
export function SpotField({ near, spot, onPick, onClear, label, search = false }: Props) {
  const { t } = useTranslation();
  const id = useId();
  const [typed, setTyped] = useState('');
  const [found, setFound] = useState<Found>(null);
  // The map open to drop a pin on, where the town is.
  const [mapping, setMapping] = useState(false);
  const lat = near?.lat;
  const lon = near?.lon;

  useEffect(() => {
    if (lat === undefined || lon === undefined || typed.trim().length < 3 || gpsOf(typed))
      return;
    const stop = new AbortController();
    const timer = setTimeout(() => {
      findSpots(typed, { lat, lon }, stop.signal).then(
        (answer) => {
          setFound({ query: typed, spots: answer.spots, osm: answer.osm });
        },
        () => {
          // An answer dropped for a newer search is for another query, and not shown.
          setFound({ query: typed, spots: [], osm: 'failed' });
        },
      );
    }, PAUSE);
    return () => {
      clearTimeout(timer);
      stop.abort();
    };
  }, [typed, lat, lon]);

  const gps = gpsOf(typed);
  const shown =
    gps === null && found !== null && found.query === typed && typed.trim().length >= 3
      ? found
      : null;

  return (
    <div className="field spot-field">
      <label className={search ? 'visually-hidden' : 'field-label'} htmlFor={id}>
        {label ?? t('stopForm.spot')}
      </label>
      {spot ? (
        <p className="spot-picked">
          <span aria-hidden="true">📍</span>
          <span>
            <strong>{spot.name}</strong>
            {spot.detail !== '' && <span className="quiet"> · {spot.detail}</span>}
          </span>
          <button
            type="button"
            className="spot-clear"
            aria-label={t('stopForm.spotClear', { name: spot.name })}
            onClick={onClear}
          >
            ×
          </button>
        </p>
      ) : (
        <>
          {/* Typed, or pinned on the map at its end. */}
          <div className={search ? 'plan-search-box spot-search' : 'input-group'}>
            {search && (
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-4-4" />
              </svg>
            )}
            <input
              id={id}
              className={search ? undefined : 'text-input'}
              type="text"
              autoComplete="off"
              value={typed}
              disabled={near === null}
              placeholder={search ? label : t('stopForm.spotHint')}
              aria-describedby={label === undefined ? `${id}-note` : undefined}
              onChange={(event) => {
                setTyped(event.target.value);
              }}
            />
            <button
              type="button"
              className={
                search
                  ? 'plan-search-clear spot-search-map'
                  : 'input-suffix input-suffix-button'
              }
              disabled={near === null}
              aria-label={t('spotMap.open')}
              title={t('spotMap.open')}
              onClick={() => {
                setMapping(true);
              }}
            >
              <MapIcon />
            </button>
          </div>
          {mapping && near && (
            <SpotMap
              near={near}
              picked={spot}
              onClose={() => {
                setMapping(false);
              }}
              onPick={(pinned) => {
                setMapping(false);
                onPick(pinned);
              }}
            />
          )}
          {label === undefined && (
            <p className="quiet town-note" id={`${id}-note`}>
              {near === null ? t('stopForm.spotFirst') : t('stopForm.spotNote')}
            </p>
          )}
          {gps && (
            <ul className="towns" aria-label={t('stopForm.spots')}>
              <li>
                <button
                  type="button"
                  className="option"
                  onClick={() => {
                    setTyped('');
                    onPick(gps);
                  }}
                >
                  <span>
                    <span className="option-city">{gps.name}</span>
                    <span className="option-where">GPS</span>
                  </span>
                </button>
              </li>
            </ul>
          )}
          {shown && shown.osm !== 'ok' && (
            <p className="quiet town-note">
              {t(shown.osm === 'off' ? 'stopForm.spotOff' : 'stopForm.spotFailed')}
            </p>
          )}
          {shown?.osm === 'ok' && shown.spots.length === 0 && (
            <p className="quiet town-note">{t('stopForm.noSpots')}</p>
          )}
          {shown && shown.spots.length > 0 && (
            <ul className="towns" aria-label={t('stopForm.spots')}>
              {shown.spots.map((found) => (
                <li key={`${found.name}-${String(found.lat)}-${String(found.lon)}`}>
                  <button
                    type="button"
                    className="option"
                    onClick={() => {
                      setTyped('');
                      onPick(found);
                    }}
                  >
                    <span>
                      <span className="option-city">{found.name}</span>
                      <span className="option-where">{found.detail}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
